import { useMemo } from "react";

import { MenuProps } from "@mui/material";
import { Nullable, Locales } from "@simplyk/common";
import { FieldValues, Path, UseFormReturn, useController } from "react-hook-form";

import { getChoiceAvailableCount, getChoiceDisabled } from "../../../../components/CustomAnswer/choices.helper";
import { FormSelectField, FormSelectItem } from "../../../../components/forms-design-system/FormSelectField";
import { FormTypography } from "../../../../components/forms-design-system/FormTypography";
import { useLocaleContext } from "../../../../contexts/LocaleContext";
import { isRequired } from "../../../../helpers/validators";
import { useTranslate } from "../../../../hooks/useTranslate";
import { getChoiceLabel } from "../../helpers/getLabel";

import { useAnswersOfParticipantsSelectedChoicesCount } from "./useAnswersOfParticipantsSelectedChoicesCount";

import { ChoiceOutput, QuestionOutput } from "@/types/trpc";

export interface QuestionFieldSelectProps<T extends FieldValues> {
  form: UseFormReturn<T>;
  name: Path<T>;
  question: QuestionOutput;
  label?: string;
  isPreview: boolean;
}

const ITEM_HEIGHT = 48;
const menuProps: Partial<MenuProps> & { "data-test"?: string } = {
  PaperProps: {
    style: {
      marginTop: "8px",
      maxHeight: ITEM_HEIGHT * 5, // We we always show 5 lines max with scrollbar visible
      overflowY: "auto",
      width: 300,
    },
  },
  MenuListProps: {
    dense: true,
  },
};

export function QuestionFieldSelect<T extends FieldValues>({
  question,
  form,
  name,
  label,
  isPreview,
}: QuestionFieldSelectProps<T>) {
  const { t } = useTranslate();
  const { locale } = useLocaleContext();
  const { control } = form;

  const {
    field: { value = [] as string[], onChange, onBlur, ref },
    fieldState: { error },
  } = useController({
    name,
    control,
    rules: isRequired(question.required, t("common", "answerRequired")),
  });

  const sortedQuestionChoices: ChoiceOutput[] | undefined = useMemo(
    () => question.choices?.slice().sort((choiceA, choiceB) => choiceA.sortIndex - choiceB.sortIndex) || undefined,
    [question.choices]
  );

  const hasMaximum = Boolean(sortedQuestionChoices?.[0]?.maximum);

  const answersOfParticipantsSelectedChoicesCount = useAnswersOfParticipantsSelectedChoicesCount(form);

  const renderValue = (choice: {
    maximum: Nullable<number>;
    id: string;
    sortIndex: number;
    choiceFields?: { id: string; body: string; locale: Locales }[];
    countAlreadyAnswered?: Nullable<number> | undefined;
  }) => {
    const label = getChoiceLabel(choice.choiceFields || [], locale);
    const maximum = choice.maximum;
    const availableCount = getChoiceAvailableCount(choice, answersOfParticipantsSelectedChoicesCount);
    const hideAvailability = choice.id === value[0];
    const showAvailability = maximum !== null && maximum !== undefined && maximum >= 0 && !hideAvailability;
    return (
      <>
        {label}{" "}
        {showAvailability && (
          <FormTypography variant="caption" data-test="choice-label-available-typography" vibe="supershy">
            {availableCount > 0
              ? t("ticketing", "choice.availableCount", { count: availableCount })
              : t("ticketing", "choice.notAvailable")}
          </FormTypography>
        )}
      </>
    );
  };

  return (
    <FormSelectField
      key={question.id}
      label={label}
      value={value[0] || ""}
      onChange={(event) => onChange([event.target.value])}
      onClear={question.required ? undefined : () => onChange([])}
      onBlur={onBlur}
      inputRef={ref}
      error={Boolean(error?.message)}
      helperText={error?.message}
      defaultValue={hasMaximum ? undefined : sortedQuestionChoices?.[0]?.id}
      data-test="question-field-select"
      MenuProps={menuProps}
      disableInjectingGlobalStyles={isPreview} // We need this to make the LivePreview smooth
      sx={{ minHeight: 40, boxSizing: "border-box" }}
      placeholder={t("common", "selectAnOptionPlaceholder")}
      required={question.required}
      renderValue={(value) => {
        const choice = sortedQuestionChoices?.find((choice) => choice.id === value);
        if (choice) {
          return renderValue(choice);
        }
        return null;
      }}
    >
      {sortedQuestionChoices?.map((choice) => {
        return (
          <FormSelectItem
            key={`choice-${choice.id}`}
            value={choice.id}
            data-test="question-field-select-item"
            disabled={getChoiceDisabled(choice, answersOfParticipantsSelectedChoicesCount)}
            sx={{ width: "100%" }}
          >
            {renderValue(choice)}
          </FormSelectItem>
        );
      })}
    </FormSelectField>
  );
}
